import { Box, Tooltip, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import React, { useEffect, useRef, useState } from 'react';

import { GUARD_ACTIONS } from '../TabPanelCondition.constants';
import { VerticalSwitch } from './VerticalSwitch';

const RelayControlContainer = styled(Box)(() => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  minHeight: '40px',
  position: 'relative',
}));

export const UzaRelayControl = ({ uzaStatus, disabled, hasPermission, onToggle, hasDevice }) => {
  const [isPending, setIsPending] = useState(false);
  const [optimisticState, setOptimisticState] = useState(null);
  const prevUzaStatusRef = useRef(uzaStatus);

  // Извлекаем бит 2 (клапан): 0 - открыт, 1 - закрыт
  const getValveState = (status) => {
    if (status === null || status === undefined || status === -1) {
      return null;
    }
    return (status & 0b100) !== 0;
  };

  const valveIsClosed = getValveState(uzaStatus);
  const hasData = valveIsClosed !== null;

  useEffect(() => {
    const prevStatus = prevUzaStatusRef.current;

    if (isPending && prevStatus !== uzaStatus) {
      const prevValveState = getValveState(prevStatus);
      const currentValveState = getValveState(uzaStatus);

      if (prevValveState !== currentValveState && currentValveState !== null) {
        setIsPending(false);
        setOptimisticState(null);
      }
    }

    prevUzaStatusRef.current = uzaStatus;
  }, [uzaStatus, isPending, optimisticState]);

  useEffect(() => {
    if (isPending) {
      const timeout = setTimeout(() => {
        setIsPending(false);
        setOptimisticState(null);
      }, 22000);

      return () => {
        clearTimeout(timeout);
      };
    }
  }, [isPending]);

  const getTooltipTitle = () => {
    if (disabled) {
      return 'Перейдите на последнее состояние';
    }
    if (!hasDevice) {
      return 'Устройство недоступно';
    }
    if (!hasData) {
      return 'Нет данных о состоянии реле';
    }
    if (!hasPermission) {
      return 'Нет прав на управление УЗА реле (требуется суперадмин)';
    }
    if (isPending) {
      return 'Отправка команды...';
    }

    const currentState = optimisticState !== null ? optimisticState : !valveIsClosed;

    return currentState ? `Клапан открыт (подача воздуха включена)` : `Клапан закрыт (подача воздуха выключена)`;
  };

  const handleChange = (event) => {
    if (disabled) {
      return;
    }

    if (onToggle && !isPending) {
      const newState = event.target.checked;

      // OPTIMISTIC UPDATE
      setOptimisticState(newState);
      setIsPending(true);

      const command = newState ? GUARD_ACTIONS.UZA_RELAY_OPEN : GUARD_ACTIONS.UZA_RELAY_CLOSE;

      onToggle(command);
    }
  };

  const isSwitchDisabled = disabled || !hasDevice || !hasPermission || !hasData || isPending;

  const displayedState = optimisticState !== null ? optimisticState : !valveIsClosed;

  return (
    <Tooltip title={getTooltipTitle()} placement='top' arrow>
      <Box
        sx={{
          display: 'inline-block',
          cursor: isSwitchDisabled ? 'not-allowed' : 'default',
          pointerEvents: 'auto',
        }}>
        <RelayControlContainer
          sx={{
            pointerEvents: isSwitchDisabled ? 'none' : 'auto',
          }}>
          <Typography variant='caption' fontSize={10}>
            вкл
          </Typography>
          <VerticalSwitch
            checked={displayedState}
            disabled={isSwitchDisabled}
            noData={!hasData}
            onChange={handleChange}
            inputProps={{ 'aria-label': 'УЗА реле управление' }}
          />
          <Typography variant='caption' fontSize={10}>
            выкл
          </Typography>
        </RelayControlContainer>
      </Box>
    </Tooltip>
  );
};
